import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import Pikaday from 'pikaday';
import { action } from '@ember/object';
import { inject } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { guidFor } from '@ember/object/internals';
import moment from 'moment-timezone';
import { safeTimeZone } from 'beebee-base/lib/utility';
import { parseTypedDate, parseCompactNumericDate } from 'beebee-base/lib/date-parse';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("<div\n  {{did-insert this.setupPickers}}\n  {{did-update this.syncOriginalDate @originalDate}}\n  class=\'datetime-wrap {{if this.pickTime \"timepicker-enable\"}} {{if this.hideDate \"hide-date\"}}\'\n>\n  <div class=\'date-wrap {{if this.hideDate \"hide\"}}\'>\n    {{! `...attributes` lands HERE ONLY, and LAST.\n        Only here: it used to be splatted onto the time input below as well, so\n        with `@pickTime` on, a host\'s `data-test-*` appeared TWICE in the DOM and\n        every consumer test using that hook matched two nodes. The time input is\n        addressed through its own `data-test-update-time`.\n        Last: `...attributes` used to come BEFORE `data-test-date-input`, and\n        later attributes win — so a host passing `data-test-date-input` was\n        silently overridden by the component\'s own copy. }}\n    <Input\n      type=\'text\'\n      id=\'datepicker{{this.elementId}}\'\n      placeholder={{this.placeholder}}\n      disabled={{@disabled}}\n      value={{this.displayDate}}\n      autocomplete=\'off\'\n      data-test-date-input\n      ...attributes\n      {{on \'input\' this.dateInput}}\n      {{on \'focusout\' this.dateFocusOut}}\n      {{on \'keydown\' this.dateKeydown}}\n    />\n  </div>\n\n  {{#if this.pickTime}}\n    <div class=\'time-wrap {{if @pickSeconds \"with-seconds\"}}\'>\n      <input\n        type=\'time\'\n        id=\'timepicker{{this.elementId}}\'\n        value={{this.timeValue}}\n        disabled={{@disabled}}\n        step={{if @pickSeconds \'1\' \'60\'}}\n        data-test-update-time\n        {{on \'input\' (perform this._updateTimeTask)}}\n        {{on \'change\' this.timeCommit}}\n        {{on \'focusout\' this.timeCommit}}\n      />\n    </div>\n  {{/if}}\n</div>\n");

var _dec, _dec2, _dec3, _dec4, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0;
function placeInVisibleArea({
  box,
  field,
  view,
  gap = 8
}) {
  const minTop = view.top + gap;
  const maxBottom = view.top + view.height - gap;
  const minLeft = view.left + gap;
  const maxRight = view.left + view.width - gap;
  let {
    top,
    left
  } = box;
  let moved = false;
  if (Number.isFinite(top) && (top < minTop || top + box.height > maxBottom)) {
    const fits = t => t >= minTop && t + box.height <= maxBottom;
    if (field && fits(field.bottom)) {
      top = field.bottom;
    } else if (field && fits(field.top - box.height)) {
      top = field.top - box.height;
    } else {
      // Taller than the visible area: the top edge wins, so the month title
      // and the arrows stay reachable.
      top = Math.max(minTop, Math.min(top, maxBottom - box.height));
    }
    moved = true;
  }
  if (Number.isFinite(left) && (left < minLeft || left + box.width > maxRight)) {
    left = Math.max(minLeft, Math.min(left, maxRight - box.width));
    moved = true;
  }
  return moved ? {
    top,
    left
  } : null;
}
let BbInputDateComponent = (_dec = task(function* (event) {
  const value = event?.target?.value;
  if (!value) return;

  // Aggiorna timeValue subito per evitare che un re-render durante il yield resetti l'input
  this.timeValue = value;
  yield timeout(500);
  if (this.isDestroying || this.isDestroyed) return;
  this._commitTime(value);
}).restartable(), _dec2 = task(function* (dateMoment) {
  yield timeout(50);
  if (this.isDestroying || this.isDestroyed) return;
  if (!dateMoment || !moment(dateMoment, this.internalFormat, true).isValid()) {
    this.originalDate = null;
    this._updatePickerTask.perform();
    return;
  }
  const zone = safeTimeZone(this.args.timeZone || 'Europe/Rome');
  let formatDate = moment(dateMoment);
  formatDate = formatDate.tz(zone, true);
  let originalDate = this.originalDate ? moment(this.originalDate) : moment();
  originalDate.year(formatDate.year()).month(formatDate.month()).date(formatDate.date());
  if (this.pickTime) {
    this._setTimeOnDate(originalDate);
  } else {
    // Quando si seleziona solo la data (senza ora), imposta mezzogiorno nel timezone
    // dell'utente per evitare che la conversione di timezone sposti il giorno
    originalDate = moment.tz({
      year: formatDate.year(),
      month: formatDate.month(),
      date: formatDate.date(),
      hour: 12,
      minute: 0,
      second: 0
    }, zone);
  }
  const formatted = originalDate.format(this.internalFormat);
  this.originalDate = formatted;
  this.args.setDate?.(formatted);
  this.args.action?.(formatted);
}), _dec3 = task(function* () {
  // Usa yield per throttling naturale
  yield timeout(50);
  if (this.isDestroying || this.isDestroyed) return;
  if (!this.pikaday) {
    return;
  }
  if (this._isUserActivelyInteracting()) {
    return;
  }
  const zone = safeTimeZone(this.args.timeZone || 'Europe/Rome');
  const date = this.originalDate && moment(this.originalDate).isValid() ? moment(this.originalDate).tz(zone) : null;
  if (date?.isValid()) {
    try {
      // 🔑 Usa stringa YYYY-MM-DD come da manuale Pikaday
      this.pikaday.setDate(date.toDate(), true);

      // Aggiorna manualmente l'input
      const formatted = date.format(this.momentFormat);
      this._updateInputValue(formatted);
    } catch (e) {
      this.log.error('input-date: date format/update failed', e);
    }
  }
}), _dec4 = task(function* (value) {
  if (!this.pikaday?.isVisible()) {
    return false;
  }

  // Per input potenzialmente incompleti o invalidi, usa il debounce
  yield timeout(1000);
  if (this.isDestroying || this.isDestroyed) return;

  // Se il campo è vuoto, cancella immediatamente
  if (!value || value === '') {
    this.args.setDate?.(null);
    this.args.action?.(null);
    this.originalDate = null;
    this._scheduleUpdate(() => this._updatePickerTask.perform(), 50);
    return;
  }

  // Controllo per date valide - se è valida, aggiorna subito
  const validation = this._parseAndValidateDate(value);
  if (validation.isValid) {
    if (this.pickTime) {
      this._setTimeOnDate(validation.date);
    } else {
      const zone = safeTimeZone(this.args.timeZone || 'Europe/Rome');
      validation.date = moment.tz({
        year: validation.date.year(),
        month: validation.date.month(),
        date: validation.date.date(),
        hour: 12,
        minute: 0,
        second: 0
      }, zone);
    }
    this.originalDate = validation.date.format(this.internalFormat);
    this._updateInputValue(validation.formatted);
    this.args.setDate?.(this.originalDate);
    this.args.action?.(this.originalDate);
    this._scheduleUpdate(() => this._updatePickerTask.perform(true), 50);

    // 🔑 Chiudi il datepicker se l'utente ha scritto una data valida
    if (this.pikaday?.isVisible()) {
      this.pikaday.hide();
    }
    return;
  }

  // Evita conflitti se l'utente sta ancora interagendo con Pikaday (dopo il debounce)
  if (this.isUserInteracting) {
    return;
  }
  this._processDateInput(value);
}).restartable(), _class = class BbInputDateComponent extends Component {
  // The example shows today's date: a fixed 2015 date read as a stale default value to users.
  get placeholder() {
    return this.args.placeholder || this.intl.t('input.date.placeholder', {
      example: moment().format('DD/MM/YYYY')
    });
  }
  get elementId() {
    if (!this._elementId) {
      this._elementId = guidFor(this);
    }
    return this._elementId;
  }
  get momentFormat() {
    return 'DD/MM/YYYY';
  }
  get displayDate() {
    if (!this.originalDate) return '';
    const m = moment(this.originalDate);
    return m.isValid() ? m.format(this.momentFormat) : '';
  }
  get internalFormat() {
    if (this.pickTime) {
      return this.args.pickSeconds ? 'YYYY-MM-DDTHH:mm:ssZ' : 'YYYY-MM-DDTHH:mmZ';
    }
    return 'YYYY-MM-DDTHH:mm:ssZ';
  }
  get hideDate() {
    return this.args.hideDate ?? false;
  }
  get pickTime() {
    return this.args.pickTime ?? false;
  }
  get language() {
    return this.args.language ?? 'it';
  }
  get yearRange() {
    return this.args.yearRange ?? 100;
  }
  constructor() {
    super(...arguments);
    _initializerDefineProperty(this, "intl", _descriptor, this);
    _initializerDefineProperty(this, "log", _descriptor2, this);
    _initializerDefineProperty(this, "pikaday", _descriptor3, this);
    _initializerDefineProperty(this, "timeValue", _descriptor4, this);
    _initializerDefineProperty(this, "originalDate", _descriptor5, this);
    _initializerDefineProperty(this, "isUserInteracting", _descriptor6, this);
    this._timeouts = [];
    _initializerDefineProperty(this, "_updateTimeTask", _descriptor7, this);
    _initializerDefineProperty(this, "_updateDateTask", _descriptor8, this);
    _initializerDefineProperty(this, "_updatePickerTask", _descriptor9, this);
    // Bound handlers riutilizzabili (creati una sola volta per istanza)
    this._onSelectMousedown = () => {
      this.isUserInteracting = true;
    };
    this._onSelectChange = () => {
      const id = setTimeout(() => {
        if (this.isDestroying || this.isDestroyed) return;
        this.isUserInteracting = false;
        this._timeouts = this._timeouts.filter(t => t !== id);
      }, 100);
      this._timeouts.push(id);
    };
    this._onSelectBlur = () => {
      const id = setTimeout(() => {
        if (this.isDestroying || this.isDestroyed) return;
        this.isUserInteracting = false;
        this._timeouts = this._timeouts.filter(t => t !== id);
      }, 50);
      this._timeouts.push(id);
    };
    this._onButtonMousedown = () => {
      this.isUserInteracting = true;
    };
    this._onButtonClick = () => {
      const id = setTimeout(() => {
        if (this.isDestroying || this.isDestroyed) return;
        this.isUserInteracting = false;
        this._timeouts = this._timeouts.filter(t => t !== id);
      }, 50);
      this._timeouts.push(id);
    };
    _initializerDefineProperty(this, "_dateInputTask", _descriptor0, this);
    if (typeof this.args.setDate !== 'function') {
      throw new Error('BbInputDateComponent requires a setDate function in args');
    }
  }
  syncOriginalDate() {
    if (this.originalDate !== this.args.originalDate) {
      this.originalDate = this.args.originalDate;
      if (this.originalDate && moment(this.originalDate).isValid()) {
        const format = this.args.pickSeconds ? 'HH:mm:ss' : 'HH:mm';
        this.timeValue = moment(this.originalDate).format(format);
      } else {
        this.timeValue = null;
      }
      this._updatePickerTask.perform();
    }
  }
  willDestroy() {
    // [regression-fix] CAT-5 — duplicate super.willDestroy() call removed; parent hook was
    // being invoked twice (bare call + optional-chaining call), risking double-teardown.
    super.willDestroy(...arguments);

    // Cleanup di tutti i timeout
    this._timeouts.forEach(id => clearTimeout(id));
    this._timeouts = [];

    // Cleanup completo per evitare memory leak
    this.isUserInteracting = false;
    this._stopViewportFollow();
    try {
      // Rimuovi tutti i listener personalizzati prima di distruggere
      if (this.pikaday?.el) {
        const pikadayEl = this.pikaday.el;
        const selects = pikadayEl.querySelectorAll('select');
        selects.forEach(el => {
          el.removeEventListener('mousedown', this._onSelectMousedown);
          el.removeEventListener('change', this._onSelectChange);
          el.removeEventListener('blur', this._onSelectBlur);
        });
        const buttons = pikadayEl.querySelectorAll('.pika-prev, .pika-next, .pika-button');
        buttons.forEach(el => {
          el.removeEventListener('mousedown', this._onButtonMousedown);
          el.removeEventListener('click', this._onButtonClick);
        });
      }
      this.pikaday?.destroy();
      this.pikaday = null;
    } catch (e) {
      this.log.error('input-date: pikaday destroy failed', e);
    }
  }
  setupPickers() {
    this.createFlatpickr();
  }
  createFlatpickr() {
    const lang = this.intl.primaryLocale?.slice(0, 2) || this.language;
    if (!this.pikaday) {
      const pikaday = new Pikaday({
        field: document.getElementById(`datepicker${this.elementId}`),
        format: this.momentFormat,
        i18n: {
          // Accessible names of the prev/next buttons (Pikaday renders them as hidden button text);
          // apps without beebee-translation (e-commerce, booking) keep the vendor English words.
          previousMonth: this.intl.exists?.('nv.calendar.prev_month') ? this.intl.t('nv.calendar.prev_month') : 'Prev',
          nextMonth: this.intl.exists?.('nv.calendar.next_month') ? this.intl.t('nv.calendar.next_month') : 'Next',
          months: moment.months(),
          weekdays: moment.weekdays(),
          weekdaysShort: moment.weekdaysShort()
        },
        yearRange: this.yearRange,
        firstDay: lang === 'en' ? 0 : 1,
        toString: date => moment(date).format(this.momentFormat),
        parse: str => moment(str, this.momentFormat).toDate(),
        onSelect: date => {
          if (date && !this.isUserInteracting) {
            this._scheduleUpdate(() => this._updateDateTask.perform(date));
          }
        },
        onClose: () => {
          // Pikaday 1.8.2 invokes onClose with NO arguments, so read the date
          // off the instance. onSelect is suppressed while the picker is open
          // (isUserInteracting === true), so this close is the propagation path.
          this.isUserInteracting = false;
          this._stopViewportFollow();
          const date = this.pikaday?.getDate();
          if (date) {
            this._scheduleUpdate(() => this._updateDateTask.perform(date));
          }
        },
        onOpen: () => {
          this.isUserInteracting = true;
          this._setupPikadayInteractionListeners();
          this._keepPickerOnScreen();
          this._startViewportFollow();
        },
        onDraw: () => {
          // Re-attach only when open: Pikaday re-renders the DOM on month/year
          // navigation so newly inserted buttons need fresh listener bindings.
          if (this.isUserInteracting) {
            this._setupPikadayInteractionListeners();
          }
        }
      });
      this.pikaday = pikaday;
      this.args.setPika?.(pikaday);
    }
    const format = this.args.pickSeconds ? 'HH:mm:ss' : 'HH:mm';
    this.timeValue = this.originalDate && moment(this.originalDate).isValid() ? moment(this.originalDate).format(format) : null;
    this._updatePickerTask.perform();
  }
  /** Emit the typed time on the date (shared by the debounced `input` task and the immediate change / focusout). */
  _commitTime(value) {
    if (!value || this.isDestroying || this.isDestroyed) return;
    let hours,
      minutes,
      seconds = 0;
    if (this.args.pickSeconds) {
      [hours, minutes, seconds] = value.split(':').map(Number);
    } else {
      [hours, minutes] = value.split(':').map(Number);
    }
    const zone = safeTimeZone(this.args.timeZone || 'Europe/Rome');
    let originalDate = this.originalDate && moment(this.originalDate).isValid() ? moment(this.originalDate) : moment();
    originalDate = originalDate.tz(zone, true).hour(hours).minute(minutes).second(seconds || 0);
    const formatted = originalDate.format(this.internalFormat);
    const timeFormat = this.args.pickSeconds ? 'HH:mm:ss' : 'HH:mm';
    this.timeValue = originalDate.format(timeFormat);
    this.originalDate = formatted;
    this.args.setDate?.(formatted);
    this.args.action?.(formatted);
  }

  // The time input committed only from the 500ms-debounced `input` task: a time typed then «Salva» clicked (or Enter)
  // was saved with the previous time (2026-10-22, same class as the date focusout). change / focusout commit NOW.
  timeCommit(event) {
    const value = event?.target?.value;
    if (!value) return;
    this._updateTimeTask.cancelAll();
    this.timeValue = value;
    this._commitTime(value);
  }
  // Metodo helper per controllare l'interazione utente
  _isUserActivelyInteracting() {
    if (!this.pikaday?.isVisible()) {
      return false;
    }
    const pikadayEl = this.pikaday.el;
    if (!pikadayEl) {
      return false;
    }

    // Controlli per vari elementi interattivi
    const interactiveSelectors = ['.pika-select-year', '.pika-select-month', '.pika-prev', '.pika-next', '.pika-button'];

    // Verifica se qualche elemento è attivo/focused
    for (const selector of interactiveSelectors) {
      const element = pikadayEl.querySelector(selector);
      if (element && (element === document.activeElement || element.matches(':focus') || element.matches(':hover'))) {
        return true;
      }
    }

    // Verifica se ci sono select aperti (dropdown)
    const selects = pikadayEl.querySelectorAll('select');
    for (const select of selects) {
      if (select.matches(':focus') || select === document.activeElement) {
        return true;
      }
    }
    return this.isUserInteracting;
  }
  // Pikaday flips the calendar above the field as soon as it would cross the
  // bottom of the viewport, but never checks that the flipped box still fits
  // above: it just writes `top = fieldTop - calendarHeight`, which goes
  // negative when there is no room either way. The month title and the
  // prev/next arrows then sit over the top edge of the screen, unreachable —
  // `body.dropdown-open { overflow: hidden }` means there is nothing to scroll
  // to get at them. Reported on "register payment" opened from an invoice.
  //
  // POP-517 — that first fix still measured the LAYOUT viewport
  // (`pageYOffset`), which is what Pikaday measures too (`innerHeight`), and
  // ran once, on open. On a phone the visible area is the VISUAL viewport:
  // tapping the date field opens the virtual keyboard right after the picker is
  // placed, the visible height shrinks and the page scrolls, and the calendar
  // stays where it was — partly below the keyboard or off the bottom edge
  // ("moving an appointment, the calendar opens partly off the screen",
  // fisiodynamic, iPhone). Both edges, both axes, against the visible area,
  // and again on every visual-viewport change while the picker is open.
  _keepPickerOnScreen() {
    const el = this.pikaday?.el;
    if (!el) return;
    const sx = window.pageXOffset || 0;
    const sy = window.pageYOffset || 0;
    const vv = window.visualViewport;
    const view = vv ? {
      top: vv.pageTop,
      left: vv.pageLeft,
      width: vv.width,
      height: vv.height
    } : {
      top: sy,
      left: sx,
      width: window.innerWidth,
      height: window.innerHeight
    };
    const fieldEl = document.getElementById(`datepicker${this.elementId}`);
    const r = fieldEl?.getBoundingClientRect?.();
    const field = r ? {
      top: r.top + sy,
      bottom: r.bottom + sy
    } : null;
    const placed = placeInVisibleArea({
      box: {
        top: parseFloat(el.style.top),
        left: parseFloat(el.style.left),
        width: el.offsetWidth || 0,
        height: el.offsetHeight || 0
      },
      field,
      view
    });
    if (!placed) return;
    if (Number.isFinite(placed.top)) el.style.top = `${placed.top}px`;
    if (Number.isFinite(placed.left)) el.style.left = `${placed.left}px`;
  }

  // POP-517 — the virtual keyboard opens AFTER Pikaday has placed the calendar
  // (it opens on focus, the keyboard follows), so a one-shot clamp on open is
  // not enough: follow the visual viewport while the picker is visible. Pikaday
  // re-anchors to the field first, then the clamp keeps it in the visible area.
  // Document scroll is followed too: the calendar hangs off <body>, so when the
  // field sits in a fixed popup a page scroll moves one and not the other, and
  // a visual-viewport `scroll` does not fire for ordinary document scrolling.
  _startViewportFollow() {
    if (this._viewportFollow) return;
    const vv = window.visualViewport;
    this._viewportFollow = () => {
      if (this.isDestroying || this.isDestroyed) return;
      if (!this.pikaday?.isVisible?.()) return;
      this.pikaday.adjustPosition?.();
      this._keepPickerOnScreen();
    };
    vv?.addEventListener('resize', this._viewportFollow);
    vv?.addEventListener('scroll', this._viewportFollow);
    window.addEventListener('scroll', this._viewportFollow, {
      passive: true
    });
  }
  _stopViewportFollow() {
    if (!this._viewportFollow) return;
    window.visualViewport?.removeEventListener('resize', this._viewportFollow);
    window.visualViewport?.removeEventListener('scroll', this._viewportFollow);
    window.removeEventListener('scroll', this._viewportFollow);
    this._viewportFollow = null;
  }

  // Setup dei listener per gli elementi interattivi di Pikaday
  _setupPikadayInteractionListeners() {
    if (!this.pikaday?.el) return;
    const pikadayEl = this.pikaday.el;

    // Rimuovi i listener precedenti prima di riaggiungerli
    const selects = pikadayEl.querySelectorAll('select');
    selects.forEach(select => {
      select.removeEventListener('mousedown', this._onSelectMousedown);
      select.removeEventListener('change', this._onSelectChange);
      select.removeEventListener('blur', this._onSelectBlur);
      select.addEventListener('mousedown', this._onSelectMousedown);
      select.addEventListener('change', this._onSelectChange);
      select.addEventListener('blur', this._onSelectBlur);
    });
    const buttons = pikadayEl.querySelectorAll('.pika-prev, .pika-next, .pika-button');
    buttons.forEach(button => {
      button.removeEventListener('mousedown', this._onButtonMousedown);
      button.removeEventListener('click', this._onButtonClick);
      button.addEventListener('mousedown', this._onButtonMousedown);
      button.addEventListener('click', this._onButtonClick);
    });
  }

  // Helper per schedulare aggiornamenti in modo sicuro
  _scheduleUpdate(callback, delay = 10) {
    const id = setTimeout(() => {
      this._timeouts = this._timeouts.filter(t => t !== id);
      if (this.isDestroying || this.isDestroyed) return;
      if (!this.isUserInteracting) {
        callback();
      }
    }, delay);
    this._timeouts.push(id);
  }
  // Unified parsing and validation of a typed date (shared with the period picker)
  _parseAndValidateDate(value) {
    return parseTypedDate(value, {
      displayFormat: this.momentFormat
    });
  }

  // Aggiorna il valore visibile dell'input
  _updateInputValue(formattedValue) {
    const inputElement = document.getElementById(`datepicker${this.elementId}`);
    if (inputElement && inputElement.value !== formattedValue) {
      inputElement.value = formattedValue;
    }
  }
  dateInput(event) {
    const value = event?.target?.value?.trim();

    // Cancella il task precedente e avvia il nuovo
    this._dateInputTask.perform(value);
  }
  dateFocusOut(event) {
    // Cancella il timeout dell'input se presente
    clearTimeout(this._inputTimeout);
    const value = event?.target?.value?.trim() || document.getElementById(`datepicker${this.elementId}`)?.value?.trim();

    // Se il campo è vuoto, cancella sempre indipendentemente da isUserInteracting
    if (!value || value === '') {
      this.args.setDate?.(null);
      this.args.action?.(null);
      this.originalDate = null;
      this._scheduleUpdate(() => this._updatePickerTask.perform(), 50);
      return;
    }

    // Evita conflitti se l'utente sta ancora interagendo con Pikaday — but only when the focus is going INTO the
    // picker (a day / month control). The picker is open whenever the field has focus (onOpen sets
    // isUserInteracting), so bailing on the flag alone skipped the commit exactly when the user typed a date and
    // clicked «Salva»: the value then landed ~110ms later through onClose, after the form had saved the old date.
    const next = event?.relatedTarget;
    if (this.isUserInteracting && next && this.pikaday?.el?.contains(next)) {
      return;
    }

    // Processa immediatamente al focus out
    this._processDateInput(value);
  }

  // Enter in the field submits the host form on the same keystroke: commit the typed date first (keydown runs
  // before the form's submit).
  dateKeydown(event) {
    if (event?.key !== 'Enter') return;
    const value = event.target?.value?.trim();
    if (value) this._processDateInput(value);
  }

  // Metodo helper per processare l'input della data
  _processDateInput(value) {
    const result = this._parseAndValidateDate(value);
    if (result.isValid) {
      if (this.pickTime) {
        this._setTimeOnDate(result.date);
      } else {
        const zone = safeTimeZone(this.args.timeZone || 'Europe/Rome');
        result.date = moment.tz({
          year: result.date.year(),
          month: result.date.month(),
          date: result.date.date(),
          hour: 12,
          minute: 0,
          second: 0
        }, zone);
      }

      // Commit NOW, not on a timer: focusout fires on the mousedown of the next control, and a click on a form's
      // «Salva» submits ~60ms before the old scheduled commit (10ms + the task's 50ms) landed, so the save read the
      // previous date and the typed one was lost (medical patient history edit, 2026-10-22). The value is already
      // normalised above (noon in the zone, or the typed time), exactly what _updateDateTask would emit.
      const formatted = result.date.format(this.internalFormat);
      this.originalDate = formatted;
      this.args.setDate?.(formatted);
      this.args.action?.(formatted);
      // keep Pikaday on the committed value NOW: its onClose (the blur hide, ~50ms later) re-emits
      // pikaday.getDate(), which would otherwise put the previous date back after the save
      try {
        this.pikaday?.setDate(result.date.toDate(), true);
      } catch (e) {
        this.log.error('input-date: picker sync failed', e);
      }
    } else {
      // Se il valore non è valido, non aggiornare
      return;
    }
    this._scheduleUpdate(() => this._updatePickerTask.perform(), 50);
  }

  // Compact numeric date parsing helper (shared lib)
  _parseCompactNumericDate(value) {
    return parseCompactNumericDate(value);
  }
  _setTimeOnDate(date) {
    const timeInput = document.getElementById(`timepicker${this.elementId}`);
    const timeValue = timeInput?.value;
    if (timeValue) {
      let hours,
        minutes,
        seconds = 0;
      if (this.args.pickSeconds) {
        [hours, minutes, seconds] = timeValue.split(':').map(Number);
      } else {
        [hours, minutes] = timeValue.split(':').map(Number);
      }
      if (!isNaN(hours) && !isNaN(minutes)) {
        date.hour(hours).minute(minutes).second(seconds || 0);
      }
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "log", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "pikaday", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "timeValue", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "originalDate", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return this.args.originalDate;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "isUserInteracting", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "syncOriginalDate", [action], Object.getOwnPropertyDescriptor(_class.prototype, "syncOriginalDate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setupPickers", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setupPickers"), _class.prototype), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "_updateTimeTask", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "timeCommit", [action], Object.getOwnPropertyDescriptor(_class.prototype, "timeCommit"), _class.prototype), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "_updateDateTask", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "_updatePickerTask", [_dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "_dateInputTask", [_dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "dateInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "dateInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "dateFocusOut", [action], Object.getOwnPropertyDescriptor(_class.prototype, "dateFocusOut"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "dateKeydown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "dateKeydown"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbInputDateComponent);

export { BbInputDateComponent as default, placeInVisibleArea };
//# sourceMappingURL=bb-input-date.js.map
